/*
  In Love: keeps the Little Loves confetti off copy (24 September 2026).

  The confetti (snippets/inlove-little-loves-sprinkles.liquid) is fixed,
  hand-placed artwork that continues across a header or card. Copy length and
  screen width vary, so any single piece could land on text. For every element
  marked [data-ll-guard], this hides each confetti piece that comes too close
  to the copy, and shows it again when there is room:

    quiet blocks  data-ll-guard="<selectors>" names the copy blocks (title and
                  intro, Browse links, a card's copy). Each block is taken as
                  the one box round all its lines, plus QUIET px, so nothing
                  tucks between lines or into a short last line.
    nearby        data-ll-guard-near="<selector>" adds elements outside the
                  root, such as the breadcrumb above a header, with QUIET px.
    everything    any other text or link inside the root keeps LINE px.
    data-ll-quiet="<px>" on the root overrides QUIET for a small space such as
    a homepage card, where 28px would clear its whole top edge.

  Ring pieces (.inlove-ll-halo__piece, placed from a block's own edges) win:
  a scene piece within RING px of one steps aside, and a ring piece ignores
  the quiet zone of the block it belongs to. Side pieces ([data-ll-side]) run
  in columns a fixed step apart and show only while beside their block, so a
  taller block lengthens the columns instead of opening gaps.

  It re-checks whenever the element changes size (width, a longer title, fonts
  loading) and on window resize.

  After the first pass it sets [data-ll-guarded], which is what reveals the
  continuation pieces (.ll-ext); without the script they stay hidden and only
  the original scene shows.

  Nothing may sit over the confetti; Berry Pop carries no text at any size.
*/
(() => {
  if (window.inloveLlGuard) return;

  const QUIET = 28;
  const RING = 10;
  const LINE = 14;

  const lineRects = (el) => {
    const rects = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.textContent.trim() || node.parentElement.closest('svg, script, style')) continue;
      const range = document.createRange();
      range.selectNodeContents(node);
      for (const r of range.getClientRects()) if (r.width && r.height) rects.push(r);
    }
    el.querySelectorAll('a, button, [aria-current], summary').forEach((child) => {
      const r = child.getBoundingClientRect();
      if (r.width && r.height) rects.push(r);
    });
    return rects;
  };

  // The one box round every line and link in a block.
  const blockRect = (el) => {
    const rects = lineRects(el);
    if (!rects.length) return null;
    return {
      left: Math.min(...rects.map((r) => r.left)),
      top: Math.min(...rects.map((r) => r.top)),
      right: Math.max(...rects.map((r) => r.right)),
      bottom: Math.max(...rects.map((r) => r.bottom)),
    };
  };

  const obstacles = (root) => {
    const list = lineRects(root).map((r) => ({ r, m: LINE, el: null }));
    const quiet = Number(root.dataset.llQuiet) || QUIET;
    const blocks = [...(root.dataset.llGuard ? root.querySelectorAll(root.dataset.llGuard) : [])];
    const near = root.dataset.llGuardNear ? [...document.querySelectorAll(root.dataset.llGuardNear)] : [];
    for (const el of [...blocks, ...near]) {
      const r = blockRect(el);
      if (r) list.push({ r, m: quiet, el });
    }
    return list;
  };

  const pieces = (root) =>
    [...root.querySelectorAll('svg.inlove-ll-sprinkles')]
      .filter((svg) => svg.getClientRects().length)
      .flatMap((svg) => [...svg.querySelectorAll(':scope > circle, :scope > rect, :scope > use, :scope > path, :scope > g > *')]);

  const near = (a, r, m) => a.right > r.left - m && a.left < r.right + m && a.bottom > r.top - m && a.top < r.bottom + m;

  const check = (root) => {
    root.setAttribute('data-ll-guarded', '');
    const ring = [...root.querySelectorAll('.inlove-ll-halo__piece')];
    const all = pieces(root);
    [...ring, ...all].forEach((el) => el.style.removeProperty('visibility'));
    const blocks = obstacles(root);
    const ringRects = [];
    for (const el of ring) {
      const a = el.firstElementChild?.getBoundingClientRect();
      if (!a || !a.width) continue;
      if (el.dataset.llSide) {
        const block = el.closest('.inlove-ll-halo')?.parentElement?.getBoundingClientRect();
        if (block && (a.top > block.bottom + 20 || a.bottom < block.top - 20)) {
          el.style.visibility = 'hidden';
          continue;
        }
      }
      // Side pieces also yield to the fixed ring pieces (corners, top, bottom),
      // which come first in the markup.
      const hit =
        blocks.some(({ r, m, el: block }) => !(block && block.contains(el)) && near(a, r, block ? m : LINE)) ||
        (el.dataset.llSide && ringRects.some((r) => near(a, r, RING)));
      if (hit) el.style.visibility = 'hidden';
      else ringRects.push(a);
    }
    for (const el of all) {
      const a = el.getBoundingClientRect();
      if (!a.width) continue;
      if (blocks.some(({ r, m }) => near(a, r, m)) || ringRects.some((r) => near(a, r, RING))) {
        el.style.visibility = 'hidden';
      }
    }
  };

  // A short timer, not an animation frame: frames pause in hidden tabs, and
  // the check only needs to follow the layout, not every frame.
  const watch = (root) => {
    if (root.llGuard) return;
    let timer = 0;
    root.llGuard = () => {
      clearTimeout(timer);
      timer = setTimeout(() => check(root), 40);
    };
    new ResizeObserver(root.llGuard).observe(root);
    document.fonts?.ready.then(root.llGuard);
    check(root);
  };

  const start = () => document.querySelectorAll('[data-ll-guard]').forEach(watch);
  // recheck() re-runs every guard now; used after editor changes and in QA.
  window.inloveLlGuard = { recheck: () => document.querySelectorAll('[data-ll-guard]').forEach(check) };
  start();
  window.addEventListener('resize', () => document.querySelectorAll('[data-ll-guard]').forEach((root) => root.llGuard?.()));
  document.addEventListener('shopify:section:load', start);
})();
